'use client'

import * as React from 'react'
import { useState } from 'react'
import { createClient } from '@/lib/supabase-browser'
import { useSearchParams } from 'next/navigation'
import Link from 'next/link'
import { GoogleSignIn } from '@/components/google-sign-in'
import { Spinner } from '@/components/ui/spinner'

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [magicLinkSent, setMagicLinkSent] = useState(false)
  const [magicLinkLoading, setMagicLinkLoading] = useState(false)

  const searchParams = useSearchParams()
  const supabase = createClient()
  const authError = searchParams.get('error')
  const resetSuccess = searchParams.get('reset') === 'ok'

  async function handlePasswordLogin(e: React.FormEvent) {
    e.preventDefault()
    setPasswordLoading(true)
    setPasswordError(null)

    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) {
      setPasswordError(error.message)
      setPasswordLoading(false)
    } else {
      window.location.href = '/stories'
    }
  }

  async function handleMagicLink() {
    if (!email) {
      setPasswordError('Enter your email above, then request a sign-in link.')
      return
    }
    setMagicLinkLoading(true)
    setPasswordError(null)
    try {
      await fetch('/api/v1/auth/request-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      setMagicLinkSent(true)
    } catch {
      setPasswordError('Could not send sign-in link. Please try again.')
    } finally {
      setMagicLinkLoading(false)
    }
  }

  const errorMessage = authError === 'expired'
    ? 'That link expired. Sign in with your password below, or request a new reset link.'
    : authError === 'auth'
      ? 'Sign in failed. Please try again.'
      : null

  return (
    <main className="min-h-screen bg-cream flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-12">
          <Link href="/" className="inline-flex items-baseline gap-1.5">
            <span lang="zh-Hant" translate="no" className="font-serif-zh text-2xl font-semibold text-gold tracking-wide">寶話</span>
            <span className="font-serif text-sm uppercase tracking-widest text-ink">Stories</span>
          </Link>
        </div>

        {resetSuccess && (
          <div className="border border-gold/30 bg-gold/5 px-4 py-3 mb-6">
            <p className="font-serif text-sm text-ink">
              Password updated. Sign in with your new password.
            </p>
          </div>
        )}

        {errorMessage && (
          <div className="border border-seal-red/30 bg-seal-red/5 px-4 py-3 mb-6">
            <p className="font-serif text-sm text-seal-red">{errorMessage}</p>
          </div>
        )}

        {/* Email + Password — Primary Auth */}
        <form onSubmit={handlePasswordLogin} className="space-y-6 mb-6">
          <div>
            <label htmlFor="login-email" className="block font-serif text-xs uppercase tracking-widest text-gold mb-2">
              Email
            </label>
            <input
              id="login-email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full bg-transparent border border-subtle px-4 py-3 font-serif text-ink focus:outline-none focus:border-gold transition-colors"
              required
              autoFocus={authError === 'expired'}
            />
          </div>

          <div>
            <div className="flex items-baseline justify-between mb-2">
              <label htmlFor="login-password" className="block font-serif text-xs uppercase tracking-widest text-gold">
                Password
              </label>
              <Link
                href="/forgot-password"
                className="font-serif text-xs text-text-muted hover:text-gold transition-colors"
              >
                Forgot password?
              </Link>
            </div>
            <input
              id="login-password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="w-full bg-transparent border border-subtle px-4 py-3 font-serif text-ink focus:outline-none focus:border-gold transition-colors"
              required
            />
          </div>

          {passwordError && (
            <div className="border border-seal-red/30 bg-seal-red/5 px-4 py-3">
              <p className="font-serif text-sm text-seal-red">{passwordError}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={passwordLoading || !email || !password}
            className="btn-primary w-full disabled:opacity-50"
          >
            {passwordLoading ? (
              <span className="inline-flex items-center justify-center gap-2">
                <Spinner className="w-4 h-4 border" />
                Signing in...
              </span>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 h-px bg-subtle" />
          <span className="font-serif text-xs uppercase tracking-widest text-text-muted">or</span>
          <div className="flex-1 h-px bg-subtle" />
        </div>

        {/* Google Sign In */}
        <div className="mb-6">
          <GoogleSignIn />
        </div>

        {/* Magic Link — MOD-199: passwordless viewers can sign in without
            knowing a password. Also useful for any user who forgot theirs. */}
        <div className="mb-8">
          {magicLinkSent ? (
            <div className="border border-gold/30 bg-gold/5 px-4 py-3">
              <p className="font-serif text-sm text-ink">
                Check your inbox — we sent a sign-in link to <strong>{email}</strong>.
              </p>
            </div>
          ) : (
            <button
              onClick={handleMagicLink}
              disabled={magicLinkLoading}
              className="w-full border border-subtle px-4 py-3 font-serif text-sm text-ink hover:border-gold transition-colors disabled:opacity-50"
            >
              {magicLinkLoading ? 'Sending...' : 'Email me a sign-in link'}
            </button>
          )}
        </div>

        <p className="text-center mt-8 font-serif text-sm text-text-muted">
          Don&apos;t have an account?{' '}
          <Link href="/signup" className="text-gold hover:underline">
            Get started
          </Link>
        </p>
      </div>
    </main>
  )
}
